import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { Clock, Users, DollarSign, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';

// --- Styled Components ---

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SurveyContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0.5rem 1rem;
  min-height: calc(100vh - 80px);
  font-family: 'Inter', sans-serif;
  color: #1e293b;
  animation: ${fadeIn} 0.4s ease-out;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.75rem;
  padding: 0.25rem 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: #0fc179;
  }
  
  svg {
    font-size: 0.75rem;
  }
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 1.25rem;
  align-items: start;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 240px;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: sticky;
  top: 0.5rem;
`;

// -- Header Section --
const SurveyHeader = styled.div`
  margin-bottom: 1rem;
  
  h1 {
    color: #0f172a;
    margin: 0 0 0.25rem 0;
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.2;
  }
  
  p.description {
    color: #64748b;
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.4;
    max-width: 800px;
  }
`;

const StatsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-top: 0.75rem;
  padding: 0.5rem 0;
  border-top: 1px solid #f1f5f9;
  
  @media (max-width: 640px) {
    gap: 0.75rem;
    flex-wrap: wrap;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  
  div.icon-box {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: ${props => props.$bg || '#f1f5f9'};
    color: ${props => props.$color || '#64748b'};
    display: flex;
    align-items: center;
    justify-content: center;
  }

  div.info {
    display: flex;
    flex-direction: column;
    
    span.value {
      font-weight: 600;
      color: #0f172a;
      font-size: 0.8rem;
    }
    span.label {
      font-size: 0.65rem;
      color: #64748b;
    }
  }
`;

// -- Cards --
const GlassCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const QuestionCard = styled(GlassCard)`
  padding: 1rem;
  
  @media (max-width: 640px) {
    padding: 0.75rem;
  }

  .question-header {
    margin-bottom: 0.75rem;
    
    .question-meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.5rem;
      
      span.number {
        font-size: 0.7rem;
        font-weight: 600;
        color: #0fc179;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        background: rgba(15, 193, 121, 0.1);
        padding: 0.15rem 0.5rem;
        border-radius: 4px;
      }

      span.required {
        color: #ef4444;
        font-size: 0.65rem;
        font-weight: 600;
        background: #fef2f2;
        padding: 0.15rem 0.4rem;
        border-radius: 4px;
      }
    }
    
    h2.question-text {
      color: #0f172a;
      font-size: 0.95rem;
      font-weight: 600;
      margin: 0;
      line-height: 1.35;
    }
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const OptionLabel = styled.label`
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: white;
  
  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }
  
  &.selected {
    border-color: #0fc179;
    background: #f0fdf4;
    
    span {
      color: #059669;
      font-weight: 600;
    }
  }
  
  input {
    width: 0.85rem;
    height: 0.85rem;
    margin-right: 0.5rem;
    accent-color: #0fc179;
  }
  
  span {
    color: #334155;
    font-size: 0.8rem;
    font-weight: 500;
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #0f172a;
  transition: all 0.15s ease;
  background: #f8fafc;
  
  &:focus {
    outline: none;
    border-color: #0fc179;
    background: white;
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #0f172a;
  min-height: 80px;
  resize: vertical;
  transition: all 0.2s ease;
  background: #f8fafc;
  line-height: 1.4;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #0fc179;
    background: white;
  }
`;

// -- Sidebar Components --
const ProgressCard = styled(GlassCard)`
  padding: 1rem;
  
  h3 {
    margin: 0 0 0.75rem 0;
    font-size: 0.85rem;
    font-weight: 600;
    color: #0f172a;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    span.percent {
      color: #0fc179;
      background: #ecfdf5;
      padding: 0.15rem 0.5rem;
      border-radius: 4px;
      font-size: 0.75rem;
    }
  }

  .progress-track {
    width: 100%;
    height: 8px; // Thicker
    background: #f1f5f9;
    border-radius: 99px;
    overflow: hidden;
    
    .progress-fill {
      height: 100%;
      background: #0fc179;
      border-radius: 99px;
      transition: width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
  }
  
  .status-text {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    color: #64748b;
    text-align: center;
  }
`;

const InfoCard = styled(GlassCard)`
  padding: 1rem;
  
  .info-row {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    border-bottom: 1px dashed #e2e8f0;
    font-size: 0.8rem;
    
    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
    &:first-child {
      padding-top: 0;
    }
    
    span.label { color: #64748b; }
    span.value { 
      font-weight: 600; 
      color: #0f172a;
    }
    
    span.value.highlight { color: #0fc179; }
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #f1f5f9;
  
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    padding: 0.6rem 1.25rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    
    &.previous {
      background: white;
      color: #64748b;
      border: 1px solid #e2e8f0;
      
      &:hover:not(:disabled) {
        background: #f8fafc;
        border-color: #cbd5e1;
        color: #0f172a;
      }
      
      &:disabled { opacity: 0.5; cursor: not-allowed; }
    }
    
    &.next {
      background: #0fc179;
      color: white;
      box-shadow: 0 4px 6px -1px rgba(15, 193, 121, 0.3);
      padding: 0.75rem 2rem;
      
      &:hover:not(:disabled) {
        background: #059669;
        transform: translateY(-1px);
        box-shadow: 0 6px 12px -1px rgba(15, 193, 121, 0.4);
      }
      
      &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; box-shadow: none; }
    }
    
    &.submit {
      background: #0fc179;
      color: white;
      box-shadow: 0 4px 6px -1px rgba(15, 193, 121, 0.3);
      padding: 0.75rem 2.5rem;
      
      &:hover:not(:disabled) {
        background: #059669;
        transform: translateY(-1px);
        box-shadow: 0 6px 12px -1px rgba(15, 193, 121, 0.4);
      }
    }
    
    svg { width: 18px; height: 18px; }
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  
  &::after {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid rgba(15, 193, 121, 0.1);
    border-top: 3px solid #0fc179;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(239, 68, 68, 0.2);
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  background: white;
  color: #0f172a;
  padding: 3rem 1.5rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  max-width: 480px;
  margin: 3rem auto;
  
  h3 {
    margin: 1.5rem 0 0.5rem 0;
    color: #0f172a;
    font-size: 1.5rem;
  }
  
  p {
    margin: 0.5rem 0 1.5rem 0;
    color: #64748b;
    font-size: 1rem;
  }
  
  button {
    background: #0fc179;
    color: white;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: #059669;
      transform: translateY(-1px);
      box-shadow: 0 4px 6px -1px rgba(15, 193, 121, 0.3);
    }
  }
`;

const SurveyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [startTime] = useState(Date.now());

  const fetchSurvey = useCallback(async () => {
    try {
      const response = await axios.get(`/surveys/${id}`);
      if (response.data.success) {
        const srv = response.data.survey;
        const normalizedQuestions = (srv.questions || []).map((q, idx) => {
          const idSafe = q.id !== undefined ? q.id : idx + 1;
          switch (q.type) {
            case 'mcq_single':
              return { ...q, id: idSafe, type: 'multiple-choice', options: q.options || [] };
            case 'mcq_multi':
              return { ...q, id: idSafe, type: 'checkbox', options: q.options || [] };
            case 'short_text':
              return { ...q, id: idSafe, type: 'text' };
            case 'number':
              return { ...q, id: idSafe, type: 'number' };
            case 'rating':
              return { ...q, id: idSafe, type: 'rating', maxRating: q.scale || q.maxRating || 5 };
            case 'dropdown':
              return { ...q, id: idSafe, type: 'dropdown', options: q.options || [] };
            case 'date':
              return { ...q, id: idSafe, type: 'date', format: q.format || 'yyyy-MM-dd' };
            case 'matrix':
              return { ...q, id: idSafe, type: 'matrix', rows: q.rows || [], columns: q.columns || [] };
            default:
              return { ...q, id: idSafe };
          }
        });
        setSurvey({ ...srv, questions: normalizedQuestions });
      } else {
        setError('Survey not found or not available');
      }
    } catch (error) {
      if (error.response && error.response.status === 400 &&
        error.response.data.message === 'Survey already completed') {
        setCompleted(true);
        setError('You have already completed this survey. Thank you for your participation!');
      } else {
        setError('Failed to load survey. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSurvey();
  }, [fetchSurvey]);

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const isCurrentQuestionAnswered = () => {
    if (!survey || !survey.questions) return false;
    const question = survey.questions[currentQuestion];
    const response = responses[question.id];

    if (question.type === 'checkbox') {
      return Array.isArray(response) && response.length > 0;
    }
    if (question.type === 'matrix') {
      const rowCount = (question.rows || []).length;
      if (!response || typeof response !== 'object') return false;
      const answeredRows = Object.keys(response).length;
      return answeredRows >= rowCount;
    }

    return response !== undefined && response !== '' && response !== null;
  };

  const validateAllRequiredQuestions = () => {
    if (!survey || !survey.questions) return false;

    for (const question of survey.questions) {
      if (question.required) {
        const response = responses[question.id];

        if (question.type === 'checkbox') {
          if (!Array.isArray(response) || response.length === 0) {
            return false;
          }
        } else {
          if (response === undefined || response === '' || response === null) {
            return false;
          }
        }
      }
    }

    return true;
  };

  const getRequiredQuestionsStatus = () => {
    if (!survey || !survey.questions) return { completed: 0, total: 0 };

    const requiredQuestions = survey.questions.filter(q => q.required);
    const completedRequired = requiredQuestions.filter(question => {
      const response = responses[question.id];

      if (question.type === 'checkbox') {
        return Array.isArray(response) && response.length > 0;
      }

      return response !== undefined && response !== '' && response !== null;
    });

    return {
      completed: completedRequired.length,
      total: requiredQuestions.length
    };
  };

  const handleNext = () => {
    if (currentQuestion < survey.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateAllRequiredQuestions()) {
      setError('Please answer all required questions before submitting.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      const submissionData = { responses, timeSpent };
      const response = await axios.post(`/surveys/${survey.id}/submit`, submissionData);

      if (response.data.success) {
        setCompleted(true);
        await refreshUser();
        setTimeout(() => {
          navigate('/surveys?completed=true&refresh=true');
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to submit survey');
      }
    } catch (error) {
      console.error('Survey submission error:', error);
      setError(error.response?.data?.message || 'Failed to submit survey. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyPress = useCallback((event) => {
    if (event.key !== 'Enter') return;

    const activeElement = document.activeElement;
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      if (activeElement.type === 'text' || activeElement.type === 'email' || activeElement.type === 'tel' ||
        activeElement.type === 'url' || activeElement.type === 'number' || activeElement.type === 'date' ||
        activeElement.tagName === 'TEXTAREA') {
        event.preventDefault();
        if (currentQuestion === survey.questions.length - 1) {
          if (validateAllRequiredQuestions()) handleSubmit();
        } else {
          const currentQuestionData = survey.questions[currentQuestion];
          if (!currentQuestionData.required || isCurrentQuestionAnswered()) handleNext();
        }
        return;
      }
    }

    event.preventDefault();
    if (currentQuestion === survey.questions.length - 1) {
      if (validateAllRequiredQuestions()) handleSubmit();
    } else {
      const currentQuestionData = survey.questions[currentQuestion];
      if (!currentQuestionData.required || isCurrentQuestionAnswered()) handleNext();
    }
  }, [currentQuestion, survey, responses, handleNext, handleSubmit, isCurrentQuestionAnswered, validateAllRequiredQuestions]);

  useEffect(() => {
    if (survey && survey.questions && survey.questions.length > 0) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [handleKeyPress, survey]);

  const formatDuration = (minutes) => {
    if (!minutes || isNaN(minutes)) return `${survey.estimatedTime || 8} min`;
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const renderQuestion = (question) => {
    const currentResponse = responses[question.id];

    switch (question.type) {
      case 'multiple-choice':
        return (
          <OptionsContainer>
            {question.options.map((option, index) => (
              <OptionLabel
                key={index}
                className={currentResponse === option ? 'selected' : ''}
              >
                <input
                  type="radio"
                  name={`question_${question.id}`}
                  value={option}
                  checked={currentResponse === option}
                  onChange={(e) => handleResponseChange(question.id, e.target.value)}
                />
                <span>{option}</span>
              </OptionLabel>
            ))}
          </OptionsContainer>
        );

      case 'checkbox':
        return (
          <OptionsContainer>
            {question.options.map((option, index) => (
              <OptionLabel
                key={index}
                className={(currentResponse || []).includes(option) ? 'selected' : ''}
              >
                <input
                  type="checkbox"
                  value={option}
                  checked={(currentResponse || []).includes(option)}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    const prevValue = responses[question.id] || [];
                    let updatedValue;
                    if (prevValue.includes(newValue)) {
                      updatedValue = prevValue.filter(v => v !== newValue);
                    } else {
                      updatedValue = [...prevValue, newValue];
                    }
                    handleResponseChange(question.id, updatedValue);
                  }}
                />
                <span>{option}</span>
              </OptionLabel>
            ))}
          </OptionsContainer>
        );

      case 'text':
        return (
          <TextInput
            type="text"
            value={currentResponse || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="Type your answer here..."
            autoFocus
          />
        );

      case 'number':
        return (
          <TextInput
            type="number"
            value={currentResponse || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="Enter a number..."
            autoFocus
          />
        );

      case 'rating':
        return (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {[...Array(question.maxRating || 5)].map((_, i) => {
              const ratingValue = i + 1;
              return (
                <button
                  key={ratingValue}
                  onClick={() => handleResponseChange(question.id, ratingValue)}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    border: currentResponse === ratingValue ? '1px solid #0fc179' : '1px solid #e2e8f0',
                    background: currentResponse === ratingValue ? '#0fc179' : 'white',
                    color: currentResponse === ratingValue ? 'white' : '#64748b',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {ratingValue}
                </button>
              );
            })}
          </div>
        );

      default:
        return (
          <TextArea
            value={currentResponse || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="Type your answer here..."
            autoFocus
          />
        );
    }
  };

  if (loading) {
    return (
      <LoadingSpinner>
        {/* Use a simple spinner or icon if you prefer */}
      </LoadingSpinner>
    );
  }

  if (completed) {
    return (
      <SurveyContainer>
        <SuccessMessage>
          <div style={{ color: '#0fc179' }}>
            <FaCheckCircle size={56} />
          </div>
          <h3>Survey Completed!</h3>
          <p>Thank you for your feedback. Your points have been added to your balance.</p>
          <button onClick={() => navigate('/surveys')}>Back to Surveys</button>
        </SuccessMessage>
      </SurveyContainer>
    );
  }

  if (error && !survey) {
    return (
      <SurveyContainer>
        <BackButton onClick={() => navigate('/surveys')}>
          <FaArrowLeft /> Back
        </BackButton>
        <ErrorMessage>
          <FaExclamationTriangle />
          {error}
        </ErrorMessage>
      </SurveyContainer>
    );
  }

  if (!survey) return null;

  const currentQuestionData = survey.questions[currentQuestion];
  const progressPercentage = ((currentQuestion) / survey.questions.length) * 100;
  const reqStatus = getRequiredQuestionsStatus();

  return (
    <SurveyContainer>
      <BackButton onClick={() => navigate('/surveys')}>
        <FaArrowLeft /> Back to Surveys
      </BackButton>

      {error && (
        <ErrorMessage>
          <FaExclamationTriangle />
          {error}
        </ErrorMessage>
      )}

      <SurveyHeader>
        <h1>{survey.title}</h1>
        <p className="description">{survey.description}</p>

        <StatsBar>
          <StatItem $bg="#ecfdf5" $color="#059669">
            <div className="icon-box">
              <Clock size={18} />
            </div>
            <div className="info">
              <span className="value">{formatDuration(survey.duration || survey.estimatedTime)}</span>
              <span className="label">Estimated time</span>
            </div>
          </StatItem>

          <StatItem $bg="#ecfeff" $color="#0891b2">
            <div className="icon-box">
              <DollarSign size={18} />
            </div>
            <div className="info">
              <span className="value">{survey.points || survey.reward || 100} points</span>
              <span className="label">Reward</span>
            </div>
          </StatItem>

          <StatItem $bg="#fff7ed" $color="#ea580c">
            <div className="icon-box">
              <Users size={18} />
            </div>
            <div className="info">
              <span className="value">General</span>
              <span className="label">Target audience</span>
            </div>
          </StatItem>
        </StatsBar>
      </SurveyHeader>

      <LayoutGrid>
        <MainContent>
          <QuestionCard>
            <div className="question-header">
              <div className="question-meta">
                <span className="number">Question {currentQuestion + 1}</span>
                {currentQuestionData.required && (
                  <span className="required">
                    Required *
                  </span>
                )}
              </div>
              <h2 className="question-text">{currentQuestionData.text || currentQuestionData.title || currentQuestionData.question}</h2>
            </div>
            <div className="question-content">
              {renderQuestion(currentQuestionData)}
            </div>

            <NavigationButtons>
              <button
                className="previous"
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
              >
                <ChevronLeft /> Previous
              </button>

              {currentQuestion < survey.questions.length - 1 ? (
                <button
                  className="next"
                  onClick={handleNext}
                  disabled={currentQuestionData.required && !isCurrentQuestionAnswered()}
                >
                  Next <ChevronRight />
                </button>
              ) : (
                <button
                  className="submit"
                  onClick={handleSubmit}
                  disabled={submitting || (currentQuestionData.required && !isCurrentQuestionAnswered())}
                >
                  {submitting ? 'Submitting...' : 'Submit Survey'}
                  {!submitting && <FaCheckCircle style={{ width: '18px', height: '18px' }} />}
                </button>
              )}
            </NavigationButtons>
          </QuestionCard>
        </MainContent>

        <Sidebar>
          <ProgressCard>
            <h3>
              Progress
              <span className="percent">{Math.round(progressPercentage)}%</span>
            </h3>
            <div className="progress-track">
              <div
                className="progress-fill"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <p className="status-text">
              Question {currentQuestion + 1} of {survey.questions.length}
            </p>
          </ProgressCard>

          <InfoCard>
            <div className="info-row">
              <span className="label">Total Questions:</span>
              <span className="value">{survey.questions.length}</span>
            </div>
            <div className="info-row">
              <span className="label">Current:</span>
              <span className="value">{currentQuestion + 1}</span>
            </div>
            <div className="info-row">
              <span className="label">Answered:</span>
              <span className="value">{Object.keys(responses).length}</span>
            </div>
            <div className="info-row">
              <span className="label">Required:</span>
              <span className="value highlight">
                {reqStatus.completed}/{reqStatus.total}
              </span>
            </div>

            {(reqStatus.completed < reqStatus.total) && (currentQuestion === survey.questions.length - 1) && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                background: '#fef2f2',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '0.75rem',
                fontWeight: '500',
                textAlign: 'center',
                lineHeight: '1.4'
              }}>
                Please complete all required questions to submit
              </div>
            )}
          </InfoCard>
        </Sidebar>
      </LayoutGrid>
    </SurveyContainer>
  );
};

export default SurveyDetail;
